import { create } from "zustand";
import type { RunStreamState } from "./useRunStream";
import { isClientGiveUp, readsInterrupted } from "../lib/runNotify";

// Global, cross-page run state (0027 P1). The run's SSE stream is owned by
// <ActiveRunProvider> at the App root (not by the Product view), so it
// survives route navigation. Keyed by productId — a founder can have
// several products running at once (concurrency = map, per the 0027
// resolved decision).
//
// Lifecycle: startRun registers a run to stream; <ActiveRunProvider>
// mounts one stream worker per non-terminal run and mirrors its state in
// via setSnapshot; the terminal snapshot (complete/error) is KEPT so the
// run menu and the toast can show how the run ended until endRun clears it
// (the product page reads the result from the run record through its next
// step, 0051 slice 2). reviveRun (0159) puts a lost or interrupted entry back
// to streaming when the server names its run live.

export type ActiveRun = {
  productId: string;
  runId?: string;
  streamUrl?: string;
  snapshot: RunStreamState;
  /** epoch ms of the last snapshot change — a proxy for "when did this run
   *  last do something". At a terminal snapshot this is ~completion time, so
   *  the run menu uses it for the "Xm ago" relative timestamp. */
  updatedAt: number;
  /** How many times reviveRun has put this entry back to streaming (0159;
   *  absent: never). The in-app completion toast keys on it with runKey, so
   *  a revived run's real end is announced again. */
  revival?: number;
};

export function isTerminalRun(s: RunStreamState): boolean {
  return s.phase === "complete" || s.phase === "error";
}

// isRunInFlight is the ONE spelling of "a run is in flight for this product"
// (0148 A35, moved here from views/Review.tsx; the covered-by row
// (CoveredByHolderRow, for its saved-draft line), the amend hook
// (hooks/useAmendDecision, which that row and the spec drawer call), the
// spec-follow hook (hooks/useSpecFollow, whose in-flight signal the map page,
// views/Graph.tsx, hands the drawer for its Amend) and the product page read it
// (the page to follow a pass its served next step names, for the first
// pass's takeover); the Review page's own check-in
// banner, which read it, is gone since 0051 slice 2;
// Product's `isRunning` is the same predicate over the same entry): the
// product's entry exists and its snapshot is not terminal. A `connecting` or
// `reconnecting` snapshot counts as in flight (the fail-safe direction).
export function isRunInFlight(runs: Record<string, ActiveRun>, pid: string): boolean {
  const r = runs[pid];
  return r !== undefined && !isTerminalRun(r.snapshot);
}

// isRevivable: the terminal readings the client made WITHOUT the server
// reporting an end it can vouch for - it gave up following the run
// (isClientGiveUp), or the run read as interrupted (readsInterrupted), which a
// heartbeat lapse can show for a run that is in fact alive (0159 N6). A
// complete snapshot, or an error whose outcome is failed, is the server's real
// end and never revives.
function isRevivable(s: RunStreamState): boolean {
  return isClientGiveUp(s) || readsInterrupted(s);
}

// Stable per-run key for "seen" tracking, shared by the run menu's unread
// badge and the completion notifier. Includes runId so a fresh run for a
// product the founder already acknowledged counts as unread again.
export function runKey(r: Pick<ActiveRun, "productId" | "runId">): string {
  return `${r.productId}:${r.runId ?? ""}`;
}

interface GlobalRunState {
  runs: Record<string, ActiveRun>;
  /** Run keys (runKey) the founder has acknowledged by opening the run menu.
   *  A terminal run whose key is absent here is "unread" and drives the
   *  header badge. */
  seenRunIds: Record<string, true>;
  /** Register a run to stream. Idempotent per (productId, runId): if we're
   *  already tracking this exact run we do nothing, so we never reset the
   *  snapshot or re-stream a run we already know about. A new runId for the
   *  same product replaces the prior entry. */
  startRun: (productId: string, opts: { runId?: string; streamUrl?: string }) => void;
  /** Follow a run the SERVER names live (0159: the products list's
   *  active_run_id, or a trigger's 409 trigger.run_in_flight hints.run_id).
   *  An entry for the same run id that the client read as lost (error,
   *  category stream_unavailable) or interrupted (error, outcome
   *  interrupted) is reset to "connecting", so <ActiveRunProvider> streams it
   *  again, and its end is announced afresh: its runKey leaves seenRunIds
   *  (the run menu's unread badge) and its revival count goes up (the
   *  toast's fired key); an entry a real run_complete or a failed run_error
   *  made terminal is never revived; no entry, or an entry for another run
   *  id, behaves exactly as startRun. */
  reviveRun: (productId: string, runId: string) => void;
  /** Mirror a worker's latest RunStreamState into the store. */
  setSnapshot: (productId: string, snapshot: RunStreamState) => void;
  /** Drop a run from the store (after its terminal state has been consumed). */
  endRun: (productId: string) => void;
  /** Mark run keys acknowledged (clears their unread badge). */
  markRunsSeen: (keys: string[]) => void;
}

export const useGlobalRunStore = create<GlobalRunState>((set, get) => ({
  runs: {},
  seenRunIds: {},
  startRun: (productId, opts) => {
    const existing = get().runs[productId];
    if (existing && existing.runId === opts.runId) {
      return; // already tracking this run (terminal or live) — don't re-stream
    }
    set((st) => ({
      runs: {
        ...st.runs,
        [productId]: {
          productId,
          runId: opts.runId,
          streamUrl: opts.streamUrl,
          snapshot: { phase: "connecting" },
          updatedAt: Date.now(),
        },
      },
    }));
  },
  reviveRun: (productId, runId) => {
    const existing = get().runs[productId];
    if (!existing || existing.runId !== runId) {
      get().startRun(productId, { runId });
      return;
    }
    if (!isRevivable(existing.snapshot)) return;
    set((st) => {
      const seenRunIds = { ...st.seenRunIds };
      delete seenRunIds[runKey(existing)];
      return {
        runs: {
          ...st.runs,
          [productId]: {
            ...existing,
            snapshot: { phase: "connecting" },
            updatedAt: Date.now(),
            revival: (existing.revival ?? 0) + 1,
          },
        },
        seenRunIds,
      };
    });
  },
  setSnapshot: (productId, snapshot) =>
    set((st) => {
      const existing = st.runs[productId];
      if (!existing) return st;
      return {
        runs: { ...st.runs, [productId]: { ...existing, snapshot, updatedAt: Date.now() } },
      };
    }),
  endRun: (productId) =>
    set((st) => {
      if (!st.runs[productId]) return st;
      const next = { ...st.runs };
      delete next[productId];
      return { runs: next };
    }),
  markRunsSeen: (keys) =>
    set((st) => {
      if (keys.length === 0) return st;
      const seenRunIds = { ...st.seenRunIds };
      for (const k of keys) seenRunIds[k] = true;
      return { seenRunIds };
    }),
}));
